//Reusable calculator component

import * as React from "react";
const { useState } = React;
import ImageButton from "./imageButton";
import styles from "./style.module.scss";

import btn_0_default from "./images/defaultNumbers/0.svg";
import btn_1_default from "./images/defaultNumbers/1.svg";
import btn_2_default from "./images/defaultNumbers/2.svg";
import btn_3_default from "./images/defaultNumbers/3.svg";
import btn_4_default from "./images/defaultNumbers/4.svg";
import btn_5_default from "./images/defaultNumbers/5.svg";
import btn_6_default from "./images/defaultNumbers/6.svg";
import btn_7_default from "./images/defaultNumbers/7.svg";
import btn_8_default from "./images/defaultNumbers/8.svg";
import btn_9_default from "./images/defaultNumbers/9.svg";
import btn_squared_default from "./images/defaultNumbers/square.svg";
import btn_plus_default from "./images/defaultNumbers/plus.svg";
import btn_minus_default from "./images/defaultNumbers/minus.svg";
import btn_divide_default from "./images/defaultNumbers/divide.svg";
import btn_multiply_default from "./images/defaultNumbers/multiply.svg";
import btn_plus_minus_default from "./images/defaultNumbers/plus_minus.svg";
import btn_dot_default from "./images/defaultNumbers/dot.svg";
import btn_equal_default from "./images/defaultNumbers/equal.svg";
import btn_AC_default from "./images/defaultNumbers/clear.svg";

import btn_0_hover from "./images/hoverNumbers/0.svg";
import btn_1_hover from "./images/hoverNumbers/1.svg";
import btn_2_hover from "./images/hoverNumbers/2.svg";
import btn_3_hover from "./images/hoverNumbers/3.svg";
import btn_4_hover from "./images/hoverNumbers/4.svg";
import btn_5_hover from "./images/hoverNumbers/5.svg";
import btn_6_hover from "./images/hoverNumbers/6.svg";
import btn_7_hover from "./images/hoverNumbers/7.svg";
import btn_8_hover from "./images/hoverNumbers/8.svg";
import btn_9_hover from "./images/hoverNumbers/9.svg";
import btn_squared_hover from "./images/hoverNumbers/square.svg";
import btn_plus_hover from "./images/hoverNumbers/plus.svg";
import btn_minus_hover from "./images/hoverNumbers/minus.svg";
import btn_divide_hover from "./images/hoverNumbers/divide.svg";
import btn_multiply_hover from "./images/hoverNumbers/multiply.svg";
import btn_plus_minus_hover from "./images/hoverNumbers/plus_minus.svg";
import btn_dot_hover from "./images/hoverNumbers/dot.svg";
import btn_equal_hover from "./images/hoverNumbers/equal.svg";
import btn_AC_hover from "./images/hoverNumbers/clear.svg";

import btn_0_pressed from "./images/pressedNumbers/0.svg";
import btn_1_pressed from "./images/pressedNumbers/1.svg";
import btn_2_pressed from "./images/pressedNumbers/2.svg";
import btn_3_pressed from "./images/pressedNumbers/3.svg";
import btn_4_pressed from "./images/pressedNumbers/4.svg";
import btn_5_pressed from "./images/pressedNumbers/5.svg";
import btn_6_pressed from "./images/pressedNumbers/6.svg";
import btn_7_pressed from "./images/pressedNumbers/7.svg";
import btn_8_pressed from "./images/pressedNumbers/8.svg";
import btn_9_pressed from "./images/pressedNumbers/9.svg";
import btn_squared_pressed from "./images/pressedNumbers/square.svg";
import btn_plus_pressed from "./images/pressedNumbers/plus.svg";
import btn_minus_pressed from "./images/pressedNumbers/minus.svg";
import btn_divide_pressed from "./images/pressedNumbers/divide.svg";
import btn_multiply_pressed from "./images/pressedNumbers/multiply.svg";
import btn_plus_minus_pressed from "./images/pressedNumbers/plus_minus.svg";
import btn_dot_pressed from "./images/pressedNumbers/dot.svg";
import btn_equal_pressed from "./images/pressedNumbers/equal.svg";
import btn_AC_pressed from "./images/pressedNumbers/clear.svg";

export function Calculator() {
  const [expression, setExpression] = useState<string>("");
  const [result, setResult] = useState<string>("0");
  const [shouldReset, setShouldReset] = useState<boolean>(false);

  const countOperators = (exp: string): number => {
    let count = 0;
    for (let i = 0; i < exp.length; i++) {
      if ("+-*/".includes(exp[i])) {
        if (i === 0 && exp[i] === "-") continue;
        count++;
      }
    }
    return count;
  };

  // Append a token (digit, operator, dot, or square) to the expression
  const appendToExpression = (token: string) => {
    let newExpression = expression;

    if (shouldReset) {
      newExpression = "";
      setShouldReset(false);
      setResult("0");
    }

    // If token is a digit:
    if (token >= "0" && token <= "9") {
      if (newExpression === "0") {
        setExpression(token);
      } else {
        setExpression(newExpression + token);
      }
      return;
    }
    // If token is a decimal point:
    if (token === ".") {
      const parts = newExpression.split(/[+\-*/]/);
      const lastOperand = parts[parts.length - 1];
      if (lastOperand.includes(".")) return; // avoid multiple dots in one operand
      setExpression(newExpression + token);
      return;
    }
    // If token is an operator:
    if ("+-*/".includes(token)) {
      // Allow a negative sign as the first character
      if (newExpression === "") {
        if (token === "-") {
          setExpression(token);
        }
        return;
      }
      const lastChar = newExpression.slice(-1);
      // If last character is already an operator, replace it
      if ("+-*/".includes(lastChar)) {
        setExpression(newExpression.slice(0, -1) + token);
        return;
      }
      // Enforce maximum of 4 operators
      if (countOperators(newExpression) >= 4) return;
      setExpression(newExpression + token);
      return;
    }
    // If token is the square symbol "²":
    if (token === "²") {
      const parts = newExpression.split(/[+\-*/]/);
      const lastOperand = parts[parts.length - 1];
      // Only allow square if there’s a valid operand and not already squared
      if (lastOperand === "" || lastOperand.endsWith("²")) return;
      setExpression(newExpression + token);
      return;
    }
  };

  // Toggle the sign of the last operand in the expression
  const handleToggleSign = () => {
    if (expression === "") return;
    // Find the last operator's index (if any)
    let lastOpIndex = -1;
    for (const op of ["+", "-", "*", "/"]) {
      const idx = expression.lastIndexOf(op);
      if (idx > lastOpIndex) lastOpIndex = idx;
    }
    // If no operator, toggle the entire expression
    if (lastOpIndex === -1) {
      if (expression.startsWith("-")) {
        setExpression(expression.slice(1));
      } else {
        setExpression("-" + expression);
      }
    } else {
      const lastOperand = expression.slice(lastOpIndex + 1);
      if (lastOperand === "") return;
      let toggled: string;
      if (lastOperand.startsWith("-")) {
        toggled = lastOperand.slice(1);
      } else {
        toggled = "-" + lastOperand;
      }
      setExpression(expression.slice(0, lastOpIndex + 1) + toggled);
    }
  };

  // Clear both expression and result
  const handleClear = () => {
    setExpression("");
    setResult("0");
  };

  // When equal is pressed, transform any "²" to valid exponentiation and evaluate
  const handleEqual = () => {
    if (expression === "") return;
    // Replace every occurrence of a number followed by the square symbol with exponentiation syntax
    const expToEval = expression.replace(/(-?\d+(\.\d+)?)²/g, "($1)**2");
    try {
      const evalResult = eval(expToEval);
      setResult(String(evalResult));
      setShouldReset(true);
    } catch (error) {
      setResult("Error");
      setShouldReset(true);
    }
  };

  // Render the calculator UI
  return (
    <div className={styles.calculatorContainer}>
      <div className={styles.calculator}>
        <div
          className={styles.calculator_display}
          style={{ backgroundImage: `url("images/Screen.svg")` }}
        >
          <div className={styles.expression_display}>
            {expression === "" ? "0" : expression}
          </div>
          <div className={styles.result_display}>{result}</div>
        </div>
        <div className={styles.calculator_keypad}>
          {/* First row */}
          <ImageButton
            onClick={() => handleClear()}
            className={styles.button}
            defaultSrc={btn_AC_default}
            hoverSrc={btn_AC_hover}
            pressedSrc={btn_AC_pressed}
            alt="Clear"
          />
          <ImageButton
            onClick={() => handleToggleSign()}
            className={styles.button}
            defaultSrc={btn_plus_minus_default}
            hoverSrc={btn_plus_minus_hover}
            pressedSrc={btn_plus_minus_pressed}
            alt="plus minus"
          />
          <ImageButton
            onClick={() => appendToExpression("²")}
            className={styles.button}
            defaultSrc={btn_squared_default}
            hoverSrc={btn_squared_hover}
            pressedSrc={btn_squared_pressed}
            alt="squared"
          />
          <ImageButton
            onClick={() => appendToExpression("/")}
            className={styles.button}
            defaultSrc={btn_divide_default}
            hoverSrc={btn_divide_hover}
            pressedSrc={btn_divide_pressed}
            alt="divide"
          />

          {/* Second row */}
          <ImageButton
            onClick={() => appendToExpression("7")}
            className={styles.button}
            defaultSrc={btn_7_default}
            hoverSrc={btn_7_hover}
            pressedSrc={btn_7_pressed}
            alt="7"
          />
          <ImageButton
            onClick={() => appendToExpression("8")}
            className={styles.button}
            defaultSrc={btn_8_default}
            hoverSrc={btn_8_hover}
            pressedSrc={btn_8_pressed}
            alt="8"
          />
          <ImageButton
            onClick={() => appendToExpression("9")}
            className={styles.button}
            defaultSrc={btn_9_default}
            hoverSrc={btn_9_hover}
            pressedSrc={btn_9_pressed}
            alt="9"
          />
          <ImageButton
            onClick={() => appendToExpression("*")}
            className={styles.button}
            defaultSrc={btn_multiply_default}
            hoverSrc={btn_multiply_hover}
            pressedSrc={btn_multiply_pressed}
            alt="multiply"
          />

          {/* Third row */}
          <ImageButton
            onClick={() => appendToExpression("4")}
            className={styles.button}
            defaultSrc={btn_4_default}
            hoverSrc={btn_4_hover}
            pressedSrc={btn_4_pressed}
            alt="4"
          />
          <ImageButton
            onClick={() => appendToExpression("5")}
            className={styles.button}
            defaultSrc={btn_5_default}
            hoverSrc={btn_5_hover}
            pressedSrc={btn_5_pressed}
            alt="5"
          />
          <ImageButton
            onClick={() => appendToExpression("6")}
            className={styles.button}
            defaultSrc={btn_6_default}
            hoverSrc={btn_6_hover}
            pressedSrc={btn_6_pressed}
            alt="6"
          />
          <ImageButton
            onClick={() => appendToExpression("-")}
            className={styles.button}
            defaultSrc={btn_minus_default}
            hoverSrc={btn_minus_hover}
            pressedSrc={btn_minus_pressed}
            alt="minus"
          />

          {/* Fourth row */}
          <ImageButton
            onClick={() => appendToExpression("1")}
            className={styles.button}
            defaultSrc={btn_1_default}
            hoverSrc={btn_1_hover}
            pressedSrc={btn_1_pressed}
            alt="1"
          />
          <ImageButton
            onClick={() => appendToExpression("2")}
            className={styles.button}
            defaultSrc={btn_2_default}
            hoverSrc={btn_2_hover}
            pressedSrc={btn_2_pressed}
            alt="2"
          />
          <ImageButton
            onClick={() => appendToExpression("3")}
            className={styles.button}
            defaultSrc={btn_3_default}
            hoverSrc={btn_3_hover}
            pressedSrc={btn_3_pressed}
            alt="3"
          />
          <ImageButton
            onClick={() => appendToExpression("+")}
            className={styles.button}
            defaultSrc={btn_plus_default}
            hoverSrc={btn_plus_hover}
            pressedSrc={btn_plus_pressed}
            alt="add"
          />

          {/* Fifth row */}
          <ImageButton
            onClick={() => appendToExpression("0")}
            className={`${styles.button} ${styles.zero}`}
            defaultSrc={btn_0_default}
            hoverSrc={btn_0_hover}
            pressedSrc={btn_0_pressed}
            alt="Clear"
          />
          <ImageButton
            onClick={() => appendToExpression(".")}
            className={styles.button}
            defaultSrc={btn_dot_default}
            hoverSrc={btn_dot_hover}
            pressedSrc={btn_dot_pressed}
            alt="decimal"
          />
          <ImageButton
            onClick={() => handleEqual()}
            className={styles.button}
            defaultSrc={btn_equal_default}
            hoverSrc={btn_equal_hover}
            pressedSrc={btn_equal_pressed}
            alt="equal"
          />
        </div>
      </div>
    </div>
  );
}
